/* ============================================
   BULLSEYE HD PARTS — Theme JS
   ============================================ */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Header scroll class ─────────────────── */
  const header = document.getElementById('site-header');
  if (header) {
    const onScroll = () => {
      header.classList.toggle('scrolled', window.scrollY > 30);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Search bar toggle ───────────────────── */
  const searchToggle = document.getElementById('search-toggle');
  const searchBar    = document.getElementById('search-bar');
  const searchClose  = document.getElementById('search-close');
  const searchInput  = document.getElementById('search-input');

  if (searchToggle && searchBar && searchClose) {
    const openSearch = () => {
      searchBar.classList.add('open');
      if (searchInput) searchInput.focus();
      searchToggle.setAttribute('aria-expanded', 'true');
    };
    const closeSearch = () => {
      searchBar.classList.remove('open');
      searchToggle.setAttribute('aria-expanded', 'false');
      searchToggle.focus();
    };

    searchToggle.addEventListener('click', () => {
      searchBar.classList.contains('open') ? closeSearch() : openSearch();
    });
    searchClose.addEventListener('click', closeSearch);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchBar.classList.contains('open')) closeSearch();
    });
  }

  /* ── Mobile menu ─────────────────────────── */
  const mobileBtn = document.getElementById('mobile-menu-btn');
  const mainNav   = document.getElementById('main-nav');

  if (mobileBtn && mainNav) {
    const setMenu = (isOpen) => {
      mainNav.classList.toggle('mobile-open', isOpen);
      mobileBtn.classList.toggle('active', isOpen);
      mobileBtn.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
      mobileBtn.setAttribute('aria-expanded', String(isOpen));
    };

    mobileBtn.addEventListener('click', () => setMenu(!mainNav.classList.contains('mobile-open')));

    /* Close on outside click or Escape */
    document.addEventListener('click', (e) => {
      if (header && !header.contains(e.target) && mainNav.classList.contains('mobile-open')) setMenu(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mainNav.classList.contains('mobile-open')) {
        setMenu(false);
        mobileBtn.focus();
      }
    });
  }

  /* ── Scroll-reveal animations ────────────── */
  if ('IntersectionObserver' in window && !reduceMotion) {
    const revealEls = document.querySelectorAll(
      '.category-card, .product-card, .stat, .mid-hero-content'
    );

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    revealEls.forEach((el) => {
      el.classList.add('reveal');
      observer.observe(el);
    });
  }

  /* ── Dropdowns: hover with delay, plus keyboard focus ─ */
  const dropdownItems = document.querySelectorAll('.nav-item.has-dropdown');

  const setDropdown = (item, isOpen) => {
    item.classList.toggle('dropdown-open', isOpen);
    const trigger = item.querySelector('.nav-link');
    if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));
  };

  dropdownItems.forEach(item => {
    let closeTimer = null;

    const open = () => {
      clearTimeout(closeTimer);
      // Close all others first
      dropdownItems.forEach(other => { if (other !== item) setDropdown(other, false); });
      setDropdown(item, true);
    };

    const close = () => {
      closeTimer = setTimeout(() => setDropdown(item, false), 120);
    };

    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', close);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) setDropdown(item, false);
    });
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        setDropdown(item, false);
        item.querySelector('.nav-link')?.focus();
      }
    });

    // Keep open while cursor is inside dropdown panel
    const panel = item.querySelector('.mega-dropdown, .dropdown');
    if (panel) {
      panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      panel.addEventListener('mouseleave', close);
    }
  });

  // Close dropdown on outside click
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.nav-item.has-dropdown')) {
      dropdownItems.forEach(el => setDropdown(el, false));
    }
  });

  /* ── Product page: photo gallery ─────────── */
  const mainImg = document.getElementById('pdp-main-img');
  const thumbs  = document.querySelectorAll('.pdp-thumb');
  if (mainImg && thumbs.length) {
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        mainImg.src    = thumb.dataset.full;
        mainImg.srcset = thumb.dataset.srcset;
        mainImg.alt    = thumb.dataset.alt;
        thumbs.forEach(t => {
          t.classList.toggle('is-active', t === thumb);
          t.toggleAttribute('aria-current', t === thumb);
        });
      });
    });
  }

  /* ── Product page: condition picker ──────── */
  // Each radio is a variant with name="id", so the form works without JS.
  // This only keeps the displayed price, SKU, button and URL in sync.
  const productForm = document.getElementById('product-form');
  if (productForm) {
    const priceEl   = document.querySelector('[data-price]');
    const compareEl = document.querySelector('[data-compare-price]');
    const skuEl     = document.querySelector('[data-sku]');
    const addBtn    = productForm.querySelector('[data-add-button]');

    productForm.addEventListener('change', (e) => {
      const input = e.target;
      if (input.name !== 'id' || input.type !== 'radio') return;
      const { price, compare, sku, available } = input.dataset;

      if (priceEl) priceEl.textContent = price;
      if (compareEl) {
        compareEl.textContent = compare;
        compareEl.hidden = !compare;
      }
      if (skuEl) skuEl.textContent = sku;
      if (addBtn) {
        const isAvailable = available === 'true';
        addBtn.disabled = !isAvailable;
        addBtn.textContent = isAvailable ? 'ADD TO CART' : 'SOLD';
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', input.value);
      window.history.replaceState({}, '', url);
    });
  }

})();

/* ── Light / dark mode toggle ──────────────
   theme.liquid sets data-theme before first paint; this switches it and remembers the choice. */
(function () {
  'use strict';
  const root = document.documentElement;
  const meta = document.querySelector('meta[name="theme-color"]');
  const toggles = document.querySelectorAll('[data-theme-toggle]');

  const apply = mode => {
    root.dataset.theme = mode;
    if (meta) meta.content = mode === 'light' ? '#F6F0E4' : '#0D0905';
    const next = mode === 'light' ? 'dark' : 'light';
    toggles.forEach(btn => {
      btn.setAttribute('aria-label', `Switch to ${next} mode`);
      const label = btn.querySelector('.theme-toggle-label');
      if (label) label.textContent = next === 'light' ? 'Light mode' : 'Dark mode';
    });
  };

  toggles.forEach(btn => btn.addEventListener('click', () => {
    const mode = root.dataset.theme === 'light' ? 'dark' : 'light';
    try { localStorage.setItem('theme', mode); } catch (e) {}
    apply(mode);
  }));

  // Follow the device setting live, until the visitor picks a mode themselves.
  window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', e => {
    let saved = null;
    try { saved = localStorage.getItem('theme'); } catch (err) {}
    if (!saved) apply(e.matches ? 'light' : 'dark');
  });

  apply(root.dataset.theme === 'light' ? 'light' : 'dark');
})();

/* ── Save to home screen banner ────────────
   Phones only, from the second visit, until dismissed or saved. ?a2hs=1 forces it (for checking).
   Tapping the banner opens the steps: no browser lets a site add itself to the home screen
   (iPhone has no API for it); Chrome on Android may offer an Install prompt instead. */
(function () {
  'use strict';
  const banner = document.getElementById('a2hs');
  if (!banner) return;

  const store = {
    get: k => { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} },
  };
  let counted = false;
  try { counted = sessionStorage.getItem('a2hs-counted'); sessionStorage.setItem('a2hs-counted', '1'); } catch (e) {}
  const visits = (+store.get('a2hs-visits') || 0) + (counted ? 0 : 1);
  store.set('a2hs-visits', String(visits));

  const forced     = new URLSearchParams(location.search).has('a2hs');
  const standalone = navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
  const phone      = window.matchMedia('(max-width: 768px)').matches && navigator.maxTouchPoints > 0;
  const ios        = /iPhone|iPad|iPod/.test(navigator.userAgent) ||
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const more    = banner.querySelector('[data-a2hs-more]');
  const guide   = banner.querySelector('.a2hs-guide');
  const install = banner.querySelector('[data-a2hs-install]');

  const close = () => { banner.hidden = true; store.set('a2hs-dismissed', '1'); };
  banner.querySelectorAll('[data-a2hs-close]').forEach(b => b.addEventListener('click', close));

  more.addEventListener('click', () => {
    guide.hidden = !guide.hidden;
    more.setAttribute('aria-expanded', String(!guide.hidden));
  });

  let installEvent = null;
  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    installEvent = e;
    install.hidden = false;
  });
  install.addEventListener('click', async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice;
    close();
  });

  guide.querySelector(ios ? '[data-a2hs-ios]' : '[data-a2hs-android]').hidden = false;

  // Footer link on phones: reopens the banner with the steps showing, even after it was dismissed.
  const reopen = document.querySelector('[data-a2hs-open]');
  if (reopen && phone && !standalone) {
    reopen.hidden = false;
    reopen.addEventListener('click', () => {
      banner.hidden = false;
      guide.hidden = false;
      more.setAttribute('aria-expanded', 'true');
    });
  }

  const eligible = !standalone && phone && visits >= 2 && !store.get('a2hs-dismissed');
  if (forced || eligible) setTimeout(() => { banner.hidden = false; }, forced ? 0 : 4000);
})();
